import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatMenuModule } from "@angular/material/menu";
import { agoLabel, CHAT_MODE_OPTIONS, nowMinuteMs, AgentThreadSummary } from "./agent-chat.models";
import {
  DockEdge,
  FloatRect,
  Viewport,
  clampFloatRect,
  defaultFloatRect,
  edgeDockRect,
  exceedsTearOff,
  expandedRect,
  innerEdge,
  movedRect,
  resizedRect,
} from "./agent-chat-float";
import { AgentChatService } from "./agent-chat.service";
import { VegaAvatarComponent } from "./vega-avatar.component";
import { AgentMessageComponent } from "./agent-message.component";
import { ViewportService } from "@app/common/services/viewport.service";
import { suggestionsForPage } from "./agent-chat-suggestions";

/** In-flight pointer gesture: where it started and the box it started from. */
interface PointerGesture {
  pointerId: number;
  startX: number;
  startY: number;
  startRect: FloatRect;
}

/** A header drag still waiting to travel far enough to pull the panel off dock. */
interface HeaderGesture extends PointerGesture {
  pendingTearOff: boolean;
}

/** A drag on one of the panel's sides, tracking which side is being pulled. */
interface ResizeGesture extends PointerGesture {
  edge: DockEdge;
}

interface ThreadHistoryGroup {
  key: string;
  label: string;
  threads: AgentThreadSummary[];
  expanded: boolean;
  saved: boolean;
}

/** Every side, in the order the handles are written into the DOM. */
const RESIZE_EDGES: { edge: DockEdge; label: string; orientation: string }[] = [
  { edge: "left", label: "Resize panel from the left edge", orientation: "vertical" },
  { edge: "top", label: "Resize panel from the top edge", orientation: "horizontal" },
  { edge: "right", label: "Resize panel from the right edge", orientation: "vertical" },
  { edge: "bottom", label: "Resize panel from the bottom edge", orientation: "horizontal" },
];

/**
 * The chat panel: a peek tab pinned to the bottom-right (Vega's avatar only until
 * hovered) that opens into a small popup docked to the bottom edge. Dragging
 * the header off the dock detaches the popup into a floating window (moved by
 * that same header, resized by pulling any of its four sides), and the header's
 * four dock buttons snap it flush to a screen edge at a third of the viewport.
 * Edge-docked, only the side facing the page can be pulled — the other three
 * sit against the screen. Closing is the only way out, and it reopens as the
 * corner popup.
 *
 * The header's popout opens the chat-only route in a new tab with the sheet
 * full-screen (see AgentChatService.popOut). In that tab the panel fills the
 * viewport with nothing behind it: the dock buttons, drag and resize handles
 * give way to a single restore control that returns to the origin page.
 */
@Component({
  selector: "tp-agent-chat-sheet",
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    AgentMessageComponent,
    VegaAvatarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./agent-chat-sheet.component.html",
  styleUrls: ["./agent-chat-sheet.component.scss"],
  host: { "(window:resize)": "onViewportResize()" },
})
export class AgentChatSheetComponent {
  protected chat = inject(AgentChatService);
  private readonly breakpoints = inject(ViewportService);

  protected draft = signal("");

  /**
   * Popout is a desktop affordance: below md a window.open tab is blocked or
   * useless on phones, and the popout tab itself offers restore instead.
   */
  protected readonly canPopOut = computed(
    () => !this.breakpoints.isCompact() && !this.chat.fullScreen(),
  );

  // ---- suggested questions ----
  // Keyed off the PAGE, not the click: a focused experiment tile still offers
  // the Experiments prompts. The bulb only exists on pages with a catalog entry.
  protected readonly pageSuggestions = computed(() =>
    suggestionsForPage(this.chat.pageTarget()?.id),
  );
  protected readonly hasSuggestions = computed(() => this.pageSuggestions().length > 0);
  /** The panel shows a blank conversation and is not about to resume an earlier one. */
  protected readonly freshChat = computed(
    () => !this.chat.resuming() && !this.chat.activeThreadId() && this.chat.messages().length === 0,
  );
  /** Page id the tray was opened for — a different page means it is closed. */
  private readonly suggestionsOpenFor = signal<string | null>(null);
  protected readonly suggestionsOpen = computed(() => {
    const id = this.chat.pageTarget()?.id;
    return !!id && this.suggestionsOpenFor() === id && this.hasSuggestions();
  });
  /**
   * Rendered height of the open tray. The panel grows by exactly this much so
   * the conversation keeps every pixel it had; zero while closed.
   */
  private readonly suggestionsHeight = signal(0);
  protected readonly sheetExtra = computed(() =>
    this.suggestionsOpen() ? this.suggestionsHeight() : 0,
  );
  private suggestionsEl = viewChild<ElementRef<HTMLElement>>("suggestionsEl");
  private suggestBulb = viewChild<ElementRef<HTMLButtonElement>>("suggestBulb");

  protected toggleSuggestions(): void {
    this.suggestionsOpenFor.set(
      this.suggestionsOpen() ? null : (this.chat.pageTarget()?.id ?? null),
    );
  }

  protected closeSuggestions(): void {
    this.suggestionsOpenFor.set(null);
  }

  /** Escape inside the tray folds it and hands focus back to the bulb. */
  protected onSuggestionsEscape(): void {
    this.closeSuggestions();
    this.suggestBulb()?.nativeElement.focus();
  }

  /** A picked suggestion is sent as-is; the tray folds away either way. */
  protected pickSuggestion(text: string): void {
    if (!this.chat.streaming()) {
      this.draft.set(text);
      this.submit();
    }
    this.closeSuggestions();
  }

  /**
   * Explicit choices only: an unseen calendar day falls back to expanded when
   * it is today and collapsed otherwise. Keeping one boolean per day (rather
   * than an accordion's single active key) lets any number of days stay open.
   */
  private readonly historyGroupState = signal<ReadonlyMap<string, boolean>>(new Map());
  protected readonly historyGroups = computed<ThreadHistoryGroup[]>(() => {
    const now = new Date(nowMinuteMs());
    const todayKey = this.localDateKey(now);
    const state = this.historyGroupState();
    const savedThreads = this.chat.threads().filter((thread) => thread.saved);
    const groups = new Map<
      string,
      { label: string; dayStart: number; threads: AgentThreadSummary[] }
    >();

    for (const thread of this.chat.threads()) {
      // Saved is exclusive: one conversation row belongs either in the
      // pinned-first Saved group or in its normal local-date group.
      if (thread.saved) continue;
      // An optimistic thread has no server timestamp until its first turn is
      // persisted. It was created now, so keep it visible with today's chats.
      const parsed = new Date(thread.updatedAt);
      const date = Number.isFinite(parsed.getTime()) ? parsed : now;
      const key = this.localDateKey(date);
      const existing = groups.get(key);
      if (existing) {
        existing.threads.push(thread);
      } else {
        groups.set(key, {
          label: date.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "2-digit",
          }),
          dayStart: new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
          ).getTime(),
          threads: [thread],
        });
      }
    }

    const dateGroups = [...groups.entries()]
      .sort(([, a], [, b]) => b.dayStart - a.dayStart)
      .map(([key, group]) => ({
        key,
        label: group.label,
        threads: group.threads,
        expanded: state.get(key) ?? key === todayKey,
        saved: false,
      }));
    return [
      {
        key: "saved",
        label: "Saved",
        threads: savedThreads,
        expanded: state.get("saved") ?? false,
        saved: true,
      },
      ...dateGroups,
    ];
  });

  protected toggleHistoryGroup(key: string, expanded: boolean): void {
    this.historyGroupState.update((state) => {
      const next = new Map(state);
      next.set(key, !expanded);
      return next;
    });
  }

  private localDateKey(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
  }

  /**
   * A scheduled run is generating into this thread. Freshness-guarded like
   * the Schedules tab's running chip: a pending flag older than any plausible
   * run means the run died — treat the row as ordinary rather than showing
   * "generating…" (and blocking delete) forever.
   */
  /** "5m" / "3h" / "Aug 12" — ages live via the shared minute tick. */
  protected threadAgo(thread: AgentThreadSummary): string {
    return agoLabel(thread.updatedAt, nowMinuteMs());
  }

  protected isGenerating(thread: AgentThreadSummary): boolean {
    if (!thread.pending) return false;
    const updated = new Date(thread.updatedAt).getTime();
    return Number.isFinite(updated) && Date.now() - updated < 15 * 60 * 1000;
  }

  /** Mode dropdown copy lives in the model file so labels stay in one place. */
  protected readonly chatModeOptions = CHAT_MODE_OPTIONS;
  protected readonly chatModeLabel = computed(
    () =>
      CHAT_MODE_OPTIONS.find((o) => o.value === this.chat.chatMode())?.label ?? "Fast",
  );
  protected readonly chatModeHint = computed(
    () => CHAT_MODE_OPTIONS.find((o) => o.value === this.chat.chatMode())?.hint ?? "",
  );
  private scrollLocked = true;

  /** Last floating box, kept across dock/undock so placement survives a dock. */
  private floatRect = signal<FloatRect | null>(null);
  /**
   * Only bound to the DOM while floating; docked placement comes from CSS.
   * Grown by the open suggestion tray so the conversation is never squeezed.
   */
  protected floatingRect = computed(() => {
    const rect =
      this.chat.isFloating() && !this.chat.fullScreen() ? this.floatRect() : null;
    return rect && expandedRect(rect, this.sheetExtra(), this.viewport());
  });

  /** The edge buttons offered in the header, in reading order. */
  protected readonly dockEdges: { edge: DockEdge; icon: string; label: string }[] = [
    { edge: "left", icon: "border_left", label: "Dock left" },
    { edge: "top", icon: "border_top", label: "Dock top" },
    { edge: "bottom", icon: "border_bottom", label: "Dock bottom" },
    { edge: "right", icon: "border_right", label: "Dock right" },
  ];

  /** Set while the panel is pinned to a screen edge; null once it floats free. */
  private dockEdge = signal<DockEdge | null>(null);
  /**
   * Size the user pulled the dock to along its own axis, in px. Null until they
   * drag the inner edge, after which it replaces the default third.
   */
  private dockSpan: number | null = null;
  /**
   * An edge only holds while floating — reopening restores the corner popup.
   * Full-screen owns the whole viewport, so no edge (and no dock button) applies.
   */
  protected activeDockEdge = computed(() =>
    this.chat.isFloating() && !this.chat.fullScreen() ? this.dockEdge() : null,
  );

  /**
   * A free window is grabbable on all four sides; a docked one only on the side
   * facing the page, since the rest are flush against the screen.
   */
  protected resizeEdges = computed(() => {
    const dock = this.activeDockEdge();
    if (!dock) return RESIZE_EDGES;
    const inner = innerEdge(dock);
    return RESIZE_EDGES.filter((handle) => handle.edge === inner);
  });

  private drag: HeaderGesture | null = null;
  private resize: ResizeGesture | null = null;

  private messageScroll = viewChild<ElementRef<HTMLElement>>("messageScroll");
  private sheetEl = viewChild<ElementRef<HTMLElement>>("sheetEl");

  constructor() {
    // Navigating away forgets the tray, so coming back to the page finds it
    // closed rather than remembering an earlier visit's choice.
    effect(() => {
      const pageId = this.chat.pageTarget()?.id ?? null;
      untracked(() => {
        if (this.suggestionsOpenFor() !== null && this.suggestionsOpenFor() !== pageId) {
          this.suggestionsOpenFor.set(null);
        }
      });
    });
    // Keep the tray's measured height current while it is open; chips re-wrap
    // as the panel is resized, so the height is observed, not computed once.
    effect((onCleanup) => {
      const el = this.suggestionsEl()?.nativeElement;
      if (!el) {
        this.suggestionsHeight.set(0);
        return;
      }
      const measure = () => this.suggestionsHeight.set(el.offsetHeight);
      measure();
      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      onCleanup(() => observer.disconnect());
    });
    // Autoscroll while streaming, unless the user scrolled up.
    effect(() => {
      this.chat.messages();
      const el = this.messageScroll()?.nativeElement;
      if (el && this.scrollLocked) {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
        });
      }
    });
  }

  protected onScroll(): void {
    const el = this.messageScroll()?.nativeElement;
    if (!el) return;
    this.scrollLocked = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }

  protected submit(): void {
    const text = this.draft();
    if (!text.trim() || this.chat.streaming()) return;
    this.draft.set("");
    this.scrollLocked = true;
    void this.chat.send(text);
  }

  protected onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  protected trackMessage(index: number): number {
    return index;
  }

  // ---- edge docks ----

  /**
   * Snap the panel flush to a screen edge at a third of the viewport. Placement is
   * still the floating window's inline geometry — the edge is what makes it
   * re-snap on window resize instead of merely being clamped back on screen.
   */
  protected dockTo(edge: DockEdge): void {
    if (this.chat.fullScreen()) return;
    // Picking a dock is a request for the standard third, so any span the user
    // had pulled a previous dock to is spent.
    this.dockSpan = null;
    this.floatRect.set(edgeDockRect(edge, this.viewport()));
    this.dockEdge.set(edge);
    this.chat.undock();
  }

  // ---- tear off dock / drag / resize ----

  /**
   * Docked, the drag starts from the popup's current on-screen box so the
   * tear-off doesn't jump; floating, it continues from wherever the user left
   * the window.
   */
  protected onHeaderPointerDown(event: PointerEvent): void {
    // Header buttons keep their own clicks; only the bar itself drags.
    if ((event.target as HTMLElement).closest("button")) return;
    // Full-screen has nowhere to drag to; the header is just a header.
    if (this.chat.fullScreen()) return;
    const floating = this.chat.isFloating();
    const startRect = floating ? this.floatRect() : this.measureDocked(this.viewport());
    if (!startRect) return;
    if (!floating) this.dockEdge.set(null);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startRect,
      pendingTearOff: !floating,
    };
  }

  /**
   * A docked panel only undocks once the drag clears the tear-off threshold —
   * a plain click on the header leaves it docked.
   */
  protected onHeaderPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (drag.pendingTearOff) {
      if (!exceedsTearOff(deltaX, deltaY)) return;
      drag.pendingTearOff = false;
      // Place the window before it floats, so it never paints unpositioned.
      this.floatRect.set(drag.startRect);
      this.chat.undock();
    }
    // Dragging an edge-docked panel pulls it off that edge into a free window.
    if (deltaX !== 0 || deltaY !== 0) this.dockEdge.set(null);
    this.floatRect.set(movedRect(drag.startRect, deltaX, deltaY, this.viewport()));
  }

  protected onHeaderPointerUp(event: PointerEvent): void {
    this.drag = this.endGesture(event, this.drag);
  }

  protected onResizePointerDown(event: PointerEvent, edge: DockEdge): void {
    const gesture = this.beginGesture(event);
    this.resize = gesture && { ...gesture, edge };
  }

  /**
   * Pulling a side only ever changes the panel's size, so a docked panel stays
   * docked — the new span is remembered and re-applied on the next window
   * resize instead of snapping back to a third.
   */
  protected onResizePointerMove(event: PointerEvent): void {
    if (!this.resize || event.pointerId !== this.resize.pointerId) return;
    const rect = resizedRect(
      this.resize.startRect,
      this.resize.edge,
      event.clientX - this.resize.startX,
      event.clientY - this.resize.startY,
      this.viewport(),
    );
    const dock = this.activeDockEdge();
    if (dock) this.dockSpan = dock === "left" || dock === "right" ? rect.width : rect.height;
    this.floatRect.set(rect);
  }

  protected onResizePointerUp(event: PointerEvent): void {
    this.resize = this.endGesture(event, this.resize);
  }

  /**
   * A shrinking window must never strand the floating panel off screen; an
   * edge-docked panel goes further and re-takes its share of the new viewport —
   * the span the user pulled it to if there is one, otherwise a third.
   */
  protected onViewportResize(): void {
    const edge = this.activeDockEdge();
    if (edge) {
      this.floatRect.set(edgeDockRect(edge, this.viewport(), this.dockSpan ?? undefined));
      return;
    }
    const rect = this.floatRect();
    if (rect) this.floatRect.set(clampFloatRect(rect, this.viewport()));
  }

  private beginGesture(event: PointerEvent): PointerGesture | null {
    const rect = this.floatRect();
    if (!this.chat.isFloating() || this.chat.fullScreen() || !rect) return null;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
    return {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startRect: rect,
    };
  }

  private endGesture(event: PointerEvent, gesture: PointerGesture | null): null {
    if (gesture && event.pointerId === gesture.pointerId) {
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    }
    return null;
  }

  /**
   * The docked popup's on-screen box, so tearing off doesn't jump or resize.
   * An open suggestion tray is subtracted back out: the stored rect is always
   * the un-grown box, and the tray is added on top of it at render time.
   */
  private measureDocked(viewport: Viewport): FloatRect {
    const box = this.sheetEl()?.nativeElement.getBoundingClientRect();
    if (!box?.width) return defaultFloatRect(viewport);
    const extra = this.sheetExtra();
    return clampFloatRect(
      { left: box.left, top: box.top + extra, width: box.width, height: box.height - extra },
      viewport,
    );
  }

  /**
   * Free-window geometry is pixel work, not a breakpoint decision, so it reads
   * the live window box rather than ViewportService's responsive signals.
   */
  private viewport(): Viewport {
    return { width: window.innerWidth, height: window.innerHeight };
  }
}
